import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Science as ScienceIcon } from "@mui/icons-material";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import json from "react-syntax-highlighter/dist/cjs/languages/hljs/json";
import { docco, atomOneDark } from "react-syntax-highlighter/dist/cjs/styles/hljs";
import { useSelector } from "react-redux";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { AGENT_TYPES } from "../../constants/AgentTypes";
import { MODEL_OPTIONS, MODEL_LABEL_BY_VALUE } from "../../constants/ModelOptions";

SyntaxHighlighter.registerLanguage("json", json);

function JsonViewer({ data }) {
  const [darkMode, setDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState(13);
  const [copyNotification, setCopyNotification] = useState(false);

  if (!data) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopyNotification(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined" size="small" onClick={handleCopy}
            sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", fontSize: "0.8125rem", "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] } }}
          >
            Copy JSON
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${SLATE[200]}`, borderRadius: "8px", overflow: "hidden" }}>
            <Button size="small" onClick={() => setFontSize((p) => Math.max(p - 2, 10))} sx={{ minWidth: 36, color: SLATE[600], textTransform: "none", borderRadius: 0, fontSize: "0.8125rem", "&:hover": { bgcolor: SLATE[100] } }}>A−</Button>
            <Box sx={{ width: "1px", height: 20, bgcolor: SLATE[200] }} />
            <Button size="small" onClick={() => setFontSize((p) => Math.min(p + 2, 24))} sx={{ minWidth: 36, color: SLATE[600], textTransform: "none", borderRadius: 0, fontSize: "0.8125rem", "&:hover": { bgcolor: SLATE[100] } }}>A+</Button>
          </Box>
        </Box>
        <FormControlLabel
          control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} size="small" sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] } }} />}
          label={<Typography sx={{ fontSize: "0.8125rem", color: SLATE[600] }}>{darkMode ? "Dark" : "Light"}</Typography>}
        />
      </Box>
      <Box sx={{ bgcolor: darkMode ? "#1a1b26" : "#ffffff", border: `1px solid ${darkMode ? "#2a2b3d" : SLATE[200]}`, borderRadius: "10px", overflow: "auto" }}>
        <SyntaxHighlighter
          language="json"
          style={darkMode ? atomOneDark : docco}
          customStyle={{ margin: 0, padding: "16px", background: "transparent", fontSize: `${fontSize}px`, lineHeight: "1.6", fontFamily: "ui-monospace, 'Cascadia Code', Menlo, Consolas, monospace" }}
          wrapLines wrapLongLines showLineNumbers
        >
          {JSON.stringify(data, null, 2)}
        </SyntaxHighlighter>
      </Box>
      <Snackbar open={copyNotification} autoHideDuration={3000} onClose={(_, r) => { if (r !== "clickaway") setCopyNotification(false); }} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" sx={{ width: "100%" }}>JSON copied to clipboard!</Alert>
      </Snackbar>
    </Box>
  );
}

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

export default function IntentTesterSidebar({ isOpen, onClose, tokens }) {
  const apps = useSelector((state) => state?.apps.list || []);
  const [testQuery, setTestQuery] = useState("");
  const [testMarket, setTestMarket] = useState("");
  const [testAgentType, setTestAgentType] = useState(AGENT_TYPES[0].value);
  const [testModel, setTestModel] = useState("gemini/gemini-2.5-flash-lite");
  const [testFallbackModel, setTestFallbackModel] = useState("openai/gpt-5.4-mini");
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [touched, setTouched] = useState({});
  const { makeRequest } = useApiCall();

  const missingFields = [
    !testQuery.trim() && "User Query",
    !testMarket && "Market",
    !testAgentType && "Agent Type",
    !testModel && "Model",
    !testFallbackModel && "Fallback Model",
  ].filter(Boolean);

  const canRun = missingFields.length === 0;

  const marketItems = useMemo(
    () => apps.map((app) => ({ value: app.market, label: app.market })),
    [apps]
  );

  const handleTestIntent = async () => {
    if (!testQuery.trim()) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const tags = [testAgentType, testMarket].filter(Boolean);
      const result = await makeRequest("/v1/intent_models/test_intent", {
        method: "POST",
        body: {
          market: testMarket || undefined,
          user_query: testQuery.trim(),
          tags,
          model: testModel,
          fallback_model: testFallbackModel,
        },
        authTokens: tokens,
      });
      setTestResult(result);
    } catch (error) {
      setTestError(error?.message || "Failed to get intent prediction.");
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTestQuery("");
    setTestMarket("");
    setTestAgentType(AGENT_TYPES[0].value);
    setTestModel("gemini/gemini-2.5-flash-lite");
    setTestFallbackModel("openai/gpt-5.4-mini");
    setTestResult(null);
    setTestError(null);
    setTouched({});
  };

  const touch = (field) => setTouched((p) => ({ ...p, [field]: true }));

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{ zIndex: 21474834 }}
      PaperProps={{
        sx: {
          width: "40%",
          minWidth: 480,
          maxWidth: 600,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: SLATE[50],
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          bgcolor: "#ffffff",
          borderBottom: `1px solid ${SLATE[200]}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ScienceIcon sx={{ fontSize: "1.25rem", color: INDIGO[500] }} />
          <Box>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "2px" }}>
              AI Models
            </Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: SLATE[900] }}>
              Test Intent
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>

        {/* Query input */}
        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            User Query <span style={{ color: "#ef4444" }}>*</span>
          </Typography>
          <TextField
            fullWidth multiline rows={3} size="small"
            placeholder='e.g. "I want to return my order"'
            value={testQuery}
            onChange={(e) => { setTestQuery(e.target.value); touch("query"); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleTestIntent(); }
            }}
            error={touched.query && !testQuery.trim()}
            helperText={touched.query && !testQuery.trim() ? "User query is required" : "Press Shift+Enter for a new line, Enter to test."}
            FormHelperTextProps={{ sx: { fontSize: "0.75rem", color: touched.query && !testQuery.trim() ? "#ef4444" : SLATE[400], mx: 0, mt: "4px" } }}
            sx={INPUT_SX}
            autoFocus
          />
        </Box>

        {/* Market + Agent Type */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Market <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              select size="small" fullWidth
              value={testMarket}
              onChange={(e) => { setTestMarket(e.target.value); touch("market"); }}
              onBlur={() => touch("market")}
              error={touched.market && !testMarket}
              helperText={touched.market && !testMarket ? "Market is required" : ""}
              sx={INPUT_SX}
              SelectProps={{ displayEmpty: true, MenuProps: { sx: { zIndex: 21474836 } } }}
            >
              <MenuItem value="" disabled><em style={{ color: SLATE[400], fontStyle: "normal" }}>Select market</em></MenuItem>
              {marketItems.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Agent Type <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              select size="small" fullWidth
              value={testAgentType}
              onChange={(e) => { setTestAgentType(e.target.value); touch("agentType"); }}
              sx={INPUT_SX}
              SelectProps={{ MenuProps: { sx: { zIndex: 21474836 } } }}
            >
              {AGENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {/* Model + Fallback Model */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Model <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <Autocomplete
              freeSolo
              size="small"
              fullWidth
              options={MODEL_OPTIONS.map((m) => m.value)}
              inputValue={testModel}
              onInputChange={(_, newInputValue) => { setTestModel(newInputValue); touch("model"); }}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[900] }}>{MODEL_LABEL_BY_VALUE[option]}</Typography>
                    <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>{option}</Typography>
                  </Box>
                </li>
              )}
              componentsProps={{ popper: { sx: { zIndex: 21474836 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type a model name"
                  error={touched.model && !testModel.trim()}
                  helperText={touched.model && !testModel.trim() ? "Model is required" : ""}
                  onBlur={() => touch("model")}
                  sx={INPUT_SX}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Fallback Model <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <Autocomplete
              freeSolo
              size="small"
              fullWidth
              options={MODEL_OPTIONS.map((m) => m.value)}
              inputValue={testFallbackModel}
              onInputChange={(_, newInputValue) => { setTestFallbackModel(newInputValue); touch("fallbackModel"); }}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[900] }}>{MODEL_LABEL_BY_VALUE[option]}</Typography>
                    <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>{option}</Typography>
                  </Box>
                </li>
              )}
              componentsProps={{ popper: { sx: { zIndex: 21474836 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or type a model name"
                  error={touched.fallbackModel && !testFallbackModel.trim()}
                  helperText={touched.fallbackModel && !testFallbackModel.trim() ? "Fallback model is required" : ""}
                  onBlur={() => touch("fallbackModel")}
                  sx={INPUT_SX}
                />
              )}
            />
          </Box>
        </Box>

        {/* Test button */}
        <Tooltip
          title={!canRun ? `Required: ${missingFields.join(", ")}` : ""}
          placement="right"
          PopperProps={{ sx: { zIndex: 21474836 } }}
        >
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={handleTestIntent}
              disabled={testing || !canRun}
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: SLATE[200], color: SLATE[400] },
              }}
            >
              {testing ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                  Testing…
                </Box>
              ) : "Run Test"}
            </Button>
          </span>
        </Tooltip>

        {/* Error */}
        {testError && (
          <Box sx={{ p: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#dc2626" }}>{testError}</Typography>
          </Box>
        )}

        {/* Result */}
        {testResult && (
          <Box>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: SLATE[700], mb: 1 }}>
              Result
            </Typography>
            <JsonViewer data={testResult} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
